import { useRef, useEffect, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {
  GraduationCap,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { APP_NAME, LeverMark } from "../../lib/brand";
import { useChatStore } from "../../stores/chat";
import { useDocumentStore } from "../../stores/document";
import { ChatMessageComponent } from "../../features/ai/ChatMessage";
import { ChatInput } from "../../features/ai/ChatInput";
import { ThinkingIndicator } from "../../features/ai/ThinkingIndicator";
import type { EditEvent } from "../../types/ai";


interface ChatPanelProps {
  assignmentId: string;
  courseName: string;
  assignmentTitle: string;
  assignmentType?: string;
  documentId?: string;
  cursorLine?: number;
  selectionStart?: number;
  selectionEnd?: number;
  selectedText?: string;
  onEdit?: (edit: EditEvent) => void;
}

const ESSAY_ACTIONS = [
  { label: "Outline", message: "Create an outline for this essay and insert it into my document" },
  { label: "Draft", message: "Write a draft based on the outline and insert it into my document" },
  { label: "Revise", message: "Read my current draft and revise it to improve quality" },
];

const PSET_ACTIONS = [
  { label: "Explain concept", message: "Explain the key concepts for this assignment" },
  { label: "Hint", message: "Give me a hint for the next problem I'm working on" },
  { label: "Check work", message: "Read my document and check my work on the current problem" },
];

const GENERAL_ACTIONS = [
  { label: "Summarize requirements", message: "Summarize the assignment requirements" },
  { label: "Get started", message: "Help me get started on this assignment - set up the document structure" },
  { label: "Improve writing", message: "Read my document and suggest improvements to my writing" },
];

function getQuickActions(assignmentType?: string) {
  if (assignmentType === "essay") return ESSAY_ACTIONS;
  if (assignmentType === "pset") return PSET_ACTIONS;
  return GENERAL_ACTIONS;
}

export function ChatPanel({
  assignmentId,
  courseName,
  assignmentTitle,
  assignmentType,
  documentId,
  cursorLine,
  selectionStart,
  selectionEnd,
  selectedText,
  onEdit,
}: ChatPanelProps) {
  const messages = useChatStore((s) => s.messages);
  const agentStatus = useChatStore((s) => s.agentStatus);
  const streamingContent = useChatStore((s) => s.streamingContent);
  const streamingThinking = useChatStore((s) => s.streamingThinking);
  const fetchHistory = useChatStore((s) => s.fetchHistory);
  const sendMessageStream = useChatStore((s) => s.sendMessageStream);
  const documents = useDocumentStore((s) => s.documents);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isProcessing = agentStatus !== "idle" && agentStatus !== "done";

  useEffect(() => {
    fetchHistory(assignmentId);
  }, [assignmentId, fetchHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const handleSend = useCallback(
    (message: string, mentionedFileIds?: string[]) => {
      sendMessageStream({
        assignmentId,
        content: message,
        documentId,
        referencedFileIds: mentionedFileIds,
        cursorLine,
        selectionStart,
        selectionEnd,
        selectedText,
        onEdit,
      });
    },
    [assignmentId, sendMessageStream, documentId, cursorLine, selectionStart, selectionEnd, selectedText, onEdit],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Context header */}
      <div className="shrink-0 border-b border-[var(--border-subtle)] px-4 py-3">
        <div className="flex items-center gap-2">
          <GraduationCap
            size={14}
            className="text-[var(--color-lever-orange)]"
          />
          <span className="text-xs font-medium text-[var(--text-secondary)]">
            {courseName}
          </span>
        </div>
        <h3 className="mt-0.5 text-sm font-medium text-[var(--text-primary)]">
          {assignmentTitle}
        </h3>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden py-1"
      >
        {messages.length === 0 && !isProcessing && (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center px-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)] text-[var(--text-muted)]">
              <LeverMark size={22} />
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {APP_NAME} agent
              </p>
              <p className="mt-1 max-w-[220px] text-xs text-[var(--text-muted)]">
                Ask questions, get help with problems, or let me edit your
                document directly.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {getQuickActions(assignmentType).map((action) => (
                <button
                  key={action.label}
                  onClick={() => handleSend(action.message)}
                  disabled={isProcessing}
                  className={cn(
                    "rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-xs",
                    "text-[var(--text-secondary)] hover:border-[var(--color-lever-orange)] hover:text-[var(--color-lever-orange)]",
                    "hover:bg-[var(--color-lever-orange)]/5",
                    "transition-all disabled:opacity-50",
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence>
          {messages.map((msg) => (
            <ChatMessageComponent key={msg.id} message={msg} />
          ))}
        </AnimatePresence>

        {isProcessing && (streamingContent || streamingThinking) && (
          <ChatMessageComponent
            message={{
              id: "streaming",
              role: "assistant",
              content: "",
              timestamp: Date.now(),
            }}
            isStreaming
            streamingContent={streamingContent}
            streamingThinking={streamingThinking}
          />
        )}

        {isProcessing && !streamingContent && !streamingThinking && (
          <ThinkingIndicator status={agentStatus} />
        )}
      </div>

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        isProcessing={isProcessing}
        placeholder="Ask lever anything..."
        files={documents.map((d) => ({ id: d.id, filename: d.filename, doc_type: d.doc_type }))}
        activeFileId={documentId}
      />
    </div>
  );
}
